import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext } from 'react';
import { COOKIES } from '~/constants';
import TagId from '~/constants/TagId';
import { useNativeDeviceType } from '~/contexts/DeviceContext';
import { useFinanceInfo } from '~/contexts/FinanceAndTradeContext';
import {
  useKeywordSuggestion,
  useSearchSource
} from '~/contexts/SearchEventsDataContext';
import { TileData } from '~/contexts/TileDataContext';
import UserContext from '~/contexts/UserContext';
import type { Vehicle } from '~/contracts';
import useGetSortByChanged from '~/hooks/useGetSortByChanged';
import usePriceDrop, { getPriceDropValue } from '~/hooks/usePriceDrop';
import BrowserCookies from '~/utilities/BrowserCookies';
import {
  getDeliveryOrPickupByVehicle,
  maybeHasGreatDealTag
} from './helpers/ResultTile.helpers';
import useDataForAnalytics from './useDataForAnalytics';

// ! When adding values, ask if they need to be added for the mega event as well.
export default function useSendTileInfoEvent() {
  const { trackEvent } = useContext(AnalyticsContext);
  const {
    partnerId,
    userLocation: { zip }
  } = useContext(UserContext);
  const {
    appliedFilters,
    currentPage,
    isDefaultFilters,
    searchRequestId,
    similarVehicles,
    sortBy,
    totalMatchedInventory,
    tradeInfo,
    vehicles
  } = useDataForAnalytics();
  const financeInfo = useFinanceInfo();
  const isInitialSortByRef = useGetSortByChanged();
  const keywordSuggestion = useKeywordSuggestion();
  const { platform, isNative } = useNativeDeviceType();
  const { restrictedState: isPriceDropRestrictedState } = usePriceDrop();
  const searchSource = useSearchSource();

  return useCallback(
    (vehicle: Partial<TileData> | undefined) => {
      const { dateAvailable, getItBy, routeId } = getDeliveryOrPickupByVehicle(
        vehicle ?? {}
      );

      const {
        isSimilarTile,
        locationId,
        price,
        previousPrice,
        vehicleId,
        vehicleTags,
        transportCost
      } = vehicle ?? {};
      const {
        apr,
        discounts,
        downPaymentDetails,
        estimatedTaxesAndFees,
        feesMinusShipping,
        monthlyPayment,
        pricingErrorMessage,
        term,
        total,
        totalTaxes
      } = price ?? {};

      const downPayment = downPaymentDetails?.totalCashDue;
      const hasTerms = downPayment != null && monthlyPayment != null;

      const tileIndex = [...vehicles, ...similarVehicles].findIndex(
        (v: Vehicle) => v.vehicleId === vehicleId
      );

      const eventData = {
        appliedFilters,
        currentPage,
        financeInfo,
        isEVTaxCredit:
          vehicle?.vehicleTags?.some(t => t.tagId === TagId.EVCredit) === true,
        isGreatDeal: maybeHasGreatDealTag(vehicle ?? {}),
        isOpenSearch: isDefaultFilters && isInitialSortByRef,
        isPriceDrop: getPriceDropValue(vehicle ?? {}),
        isPriceDropRestrictedState,
        isSimilarTile,
        isWebview: isNative,
        isZeroDown: vehicle?.price?.downPayment === 0,
        keywordSuggestion,
        locationId,
        partnerId,
        searchRequestId,
        searchSource,
        sortBy,
        tileIndex,
        tileInfo: {
          apr,
          discounts,
          downPayment: hasTerms ? Number(downPayment) : downPayment,
          estimatedTaxesAndFees: estimatedTaxesAndFees ?? null,
          feesMinusShipping,
          getItBy,
          getItByDateIso: dateAvailable,
          hasTerms,
          monthlyPayment,
          nonRefundableShippingFees:
            downPaymentDetails?.nonRefundableShippingFees,
          pricingErrorMessage,
          previousPrice,
          routeId,
          shippingCost: transportCost,
          strickerPrice: total,
          totalTaxes,
          term
        },
        totalMatchedInventory,
        tradesInfo: {
          applyTradeIn: tradeInfo?.applyTradeIn,
          status:
            tradeInfo?.status && tradeInfo?.totalTradeInValue === 0
              ? 'offer pending'
              : tradeInfo?.status,
          totalTradeInEquity: tradeInfo?.totalTradeInEquity,
          totalTradeInValue: tradeInfo?.totalTradeInValue
        },
        vehicleId,
        vehicleTags,
        webview: isNative
          ? { platform, version: BrowserCookies.get(COOKIES.nativeAppVersion) }
          : null,
        zip
      };

      trackEvent('clicked-tile-finance-terms-info', eventData);

      datadogRum.addAction('clicked-tile-finance-terms-info', eventData);
    },
    [
      appliedFilters,
      currentPage,
      financeInfo,
      isDefaultFilters,
      isInitialSortByRef,
      keywordSuggestion,
      partnerId,
      searchRequestId,
      searchSource,
      sortBy,
      isPriceDropRestrictedState,
      totalMatchedInventory,
      trackEvent,
      tradeInfo?.applyTradeIn,
      tradeInfo?.totalTradeInEquity,
      tradeInfo?.totalTradeInValue,
      vehicles,
      zip
    ]
  );
}
